import { ErrorWithCode } from "@cervval.js/common/src/errors/errors";

export interface SelectOrderByOptions {

    /** 
     * ["lastName","givenName DESC"] => "ORDER BY `lastName`, `givenName` DESC", 
     * * if none passed the table data defaultOrderBy will be used 
     */
    orderBy?: string[];
}

export interface SelectDistinctOptions {

    /** 
     * if distinct is true, the DISTINCT keyword is added to the query
     */
    distinct?: boolean;
}

/** 
 * By default, fetchConfigurations fetch the default associations (the ones that don't have doNotRetrieveByDefault)
 * With FetchAssociationsOptions you can choose which associations you want to fetch for a specific fetch configuration
 */
export interface FetchAssociationsOptions {

    /** 
     * ignored associations are not added to the SELECT query
     */
    ignoredAssociations?: string[];
    /** 
     * additional associations are added to the SELECT query even if they have doNotRetrieveByDefault
     */
    additionalAssociations?: string[];
}

export interface FetchDataIntersectIntervalFilter<keyOfDTO = string> {
    type: "intersect_interval";
    /** a table could be filtered on several intervals (ex: planned dates, effective dates) */
    intervalName: string;
    /** name of the column containing startDate */
    startDate: keyOfDTO;
    /** name of the column containing finishDate */
    finishDate: keyOfDTO;
    /** * if acceptNullValue, a null value in the database is considered as 'infinite start or finish' */
    acceptNullValue?: false | "start" | "finish" | "both";
    /** 
     * if the filter is mandatory and variables are not provided, the request will return no result 
     * "both" means start AND finish are both mandatory, false means none is mandatory
     */
    // TODO mandatory?: false | "both";
    /** negative => comparison surrounded by NOT(...) */
    // TODO negative?: boolean;
}

export interface FetchDataDateInIntervalFilter<keyOfDTO = string> {
    type: "date_in_interval";
    /** a table could be filtered on several intervals (ex: planned dates, effective dates) */
    intervalName: string;
    /** default is true */
    includeStart?: boolean;
    /** default is false */
    includeFinish?: boolean;
    /** name of the column containing date */
    date: keyOfDTO;
    /** if the filter is mandatory and variables are not provided, the request will return no result */
    // TODO mandatory?: false | "both"; always mandatory for now
    /** negative => comparison surrounded by NOT(...) */
    // TODO negative?: boolean;
}

export interface FetchDataColumnFilter<keyOfDTO = string, keyOfFetchDataVariables = string> {
    type: "=" | "IN" | ">" | "<" | "<=" | ">=";// TODO | "<>" | "NOT IN";
    /** the variable to match */
    variableName: keyOfFetchDataVariables;
    /** the column containing the value to filter */
    column: keyOfDTO;
    /** if the filter is mandatory and variable is not provided, the request will return no result */
    mandatory?: boolean;
    /** negative => comparison surrounded by NOT(...) */
    // TODO negative?: boolean;
}

export interface FetchDataDeletedFilter<keyOfFetchDataVariables = string> {
    type: "deletionMarker";
    /** the variable containing the deletion min date (we only ignore elements archived before this date), if none all archived elements are ignored */
    minDateVariableName: keyOfFetchDataVariables | undefined;
    /** if strict, an element deleted on minDate will not be returned */
    strict?: boolean;
    /** if the filter is mandatory and variable is not provided, the request will return no result */
    mandatory?: boolean;
    /** 
     * The variable containing the value of retrieveDeleted
     * If value is true, ALL deleted/archived elements will be returned 
     */
    shouldRetrieveDeletedVariableName?: keyOfFetchDataVariables;
    /** negative => comparison surrounded by NOT(...) */
    // TODO negative?: boolean;
}

/** 
 * This Filter uses the filters defined on another linked table
 * If one row in the linked table references one line of "this" table, the row is kept
 * The resulting filter is equivalent to WHERE {{thisTableColumn}} IN (SELECT linkedTableColumn FROM linkedTable WHERE linkedTableFilters)
 */
export interface FetchDataLinkedTableFilter<keyOfDTO = string> {
    type: "linkedTable";

    /**
     * Columns that are used in the link
     * most of the time it is simply thisTableColumn : "id", linkedTableColumn: "thisTableId"
     */
    thisTableColumn: keyOfDTO;
    linkedTableColumn: string;

    // name of the linked table
    linkedTable: string;

    /** process subquery in a separate query (improves performance in some complex cases) */
    separateQuery?: boolean;

}

/** This filter enables check for null or not null values */
export interface FetchDataNullableFilters<keyOfDTO = string> {
    type: "isNull" | "isNotNull";

    /** Column concerned */
    column: keyOfDTO;
}

/** 
 * This filter allows you to combine filters with a OR or AND criteria
 * * AND operator is useful only if you want to have a AND condition within a OR condition, at root level all filters are combined with a AND and you don't need to use this filter
 */
export interface FetchDataMultipleFilters<keyOfDTO = string> {
    type: "OR" | "AND";
    /**
     * Filters to combine with a OR/AND operator
     * ! FetchDataDeletedFilter is not compatible with FetchDataMultipleFilters
     */
    filters: FetchDataFilter<keyOfDTO>[];
}

/** Filter definition for standard fetchData requests */
export type FetchDataFilter<keyOfDTO = string, keyOfFetchDataVariables = string> = FetchDataColumnFilter<keyOfDTO, keyOfFetchDataVariables> |
    FetchDataIntersectIntervalFilter<keyOfDTO> | FetchDataDateInIntervalFilter<keyOfDTO> | FetchDataMultipleFilters<keyOfDTO>
    | FetchDataDeletedFilter<keyOfFetchDataVariables> | FetchDataLinkedTableFilter<keyOfDTO> | FetchDataNullableFilters<keyOfDTO>;

/** fetchData related parameters defined in the TableDefinition */
export interface FetchDataDefinition<keyOfDTO = string, keyOfFetchDataVariables = string> {
    selectOptions?: SelectOrderByOptions & SelectDistinctOptions & FetchAssociationsOptions;
    filters?: FetchDataFilter<keyOfDTO, keyOfFetchDataVariables>[];
    // TODO add permissions settings
}

/** Parameters of fetchData function */
export interface FetchDataParams<FetchDataUid = string, FetchDataVariables = Record<string, unknown>, ElementTypeName = string> {
    uid: FetchDataUid;
    variables: FetchDataVariables;
    requested: {
        type: ElementTypeName
        // TODO add differential loading infos (segments, load timestamps...)
    }[];
}

interface FetchDataSingleTableResults<ElementTypeName = string, ElementType = unknown> {
    type: ElementTypeName;
    rows: ElementType[];
    // TODO add differential loading infos (segments, load timestamps...)
    error?: ErrorWithCode;
}

export interface FetchDataResults {
    uid: string;
    data: FetchDataSingleTableResults[];
    errors: unknown[];
}

/**
 * List of fetchData requests the client can do and list of valid request parameters
 * If the config has no marshaled variable and no interval, you don't need to define a configuration
 */
export type FetchDataConfigurations<FieldTypeName = string> = {
    [uid: string]: {
        variables: {
            /** 
             * Variables that need to be marshalled (we need their varType) or that that belong to an interval need to be here, 
             * other variables used in filters don't necessarily need to be defined here
             */

            /** The name of the variable */
            name: string,
            /** var type (in order to find appropriate marshaller) */
            varType: FieldTypeName,
            /**
             * If this variable is mandatory and not found in request, the request fails and en error is returned to the client (Promise rejection)
             * Note that this behavior is different from the mandatory property of FetchDataFilters that fail silently and return an empty array
             */
            mandatory: boolean
        }[],
        /** intervalUid => name of start and finish variables */
        intervals?: Record<string, { startVariableName: string, finishVariableName: string }>
        // TODO add permission options
    }
};